import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Icon, FormActions } from '../../../components';
import type { IconType } from '../../../components';
import { Formik, Form } from 'formik';
import type { FormikValues } from 'formik';
import { Avatar } from 'flowbite-react';
import { RootState, useAppSelector } from '../../../redux';
import { Setting, User } from '../../../models';
import logo from '../../../assets/images/logo.png';

import AuthForm from './AuthForm';
import OpenAiForm from './OpenAiForm';
import AppriseForm from './AppriseForm';
import TicketingForm from './TicketingForm';
import OutlineForm from './OutlineForm';
import GitlabForm from './GitlabForm';
import SentryForm from './SentryForm';
import MetabaseForm from './MetabaseForm';
import LanguageToolForm from './LanguageToolForm';
import MileageForm from './MileageForm';
import ClientAreaForm from './ClientAreaForm';
import RemindersForm from './RemindersForm';
import TrashRetentionForm from './TrashRetentionForm';
import EmailIntakeForm from './EmailIntakeForm';
import SignalIntakeForm from './SignalIntakeForm';
import SprintsForm from './SprintsForm';
import ReplyTemplatesForm from './ReplyTemplatesForm';
import QuoteTextTemplatesForm from './QuoteTextTemplatesForm';

interface SettingFormProps {
    isCreation: boolean,
    initialValues: FormikValues,
    onSubmit: (values: AnyObj) => void
}

type TabItem = [string, IconType];
type TabGroup = { id: string; items: TabItem[] };

const TAB_IDS = new Set([
    'auth',
    'openai',
    'reminders',
    'trash_retention',
    'sprints',
    'apprise',
    'ticketing',
    'email_intake',
    'signal_intake',
    'reply_templates',
    'quote_text_templates',
    'gitlab',
    'sentry',
    'metabase',
    'languagetool',
    'mileage',
    'outline',
    'client_area',
]);

function SettingForm(props: SettingFormProps) {
    const { isCreation, initialValues, onSubmit } = props;

    const { t }                     = useTranslation();
    const [searchParams]            = useSearchParams();
    const { updating }              = useAppSelector((state: RootState) => state.settings);
    const { user }                  = useAppSelector((state: RootState) => state.auth);
    const requestedTab              = searchParams.get('tab') || '';
    const [activeTab, setActiveTab] = useState(TAB_IDS.has(requestedTab) ? requestedTab : 'auth');

    useEffect(() => {
        if (TAB_IDS.has(requestedTab))
            setActiveTab(requestedTab);
    }, [requestedTab]);

    const validate = (values: AnyObj) => {
        return {};
    }

    const setting = new Setting(initialValues);

    const groups: TabGroup[] = [
        {
            id: 'access',
            items: [
                ['auth', 'lock'],
            ],
        },
        {
            id: 'automation',
            items: [
                ['openai', 'notes'],
                ['reminders', 'reminder'],
                ['trash_retention', 'delete'],
                ['sprints', 'sprint'],
            ],
        },
        {
            id: 'communication',
            items: [
                ['apprise', 'email'],
                ['ticketing', 'ticket'],
                ['email_intake', 'email'],
                ['signal_intake', 'signal'],
                ['reply_templates', 'reply'],
                ['quote_text_templates', 'notes'],
            ],
        },
        {
            id: 'integrations',
            items: [
                ['gitlab', 'commit'],
                ['sentry', 'bug'],
                ['metabase', 'chart'],
                ['languagetool', 'lint'],
                ['mileage', 'timer'],
                ['outline', 'notes'],
                ['client_area', 'user'],
            ],
        },
    ];

    // Admin-only sprint planning tab: hide from non-admins
    const visibleGroups = groups
        .map((group) => ({
            ...group,
            items: group.items.filter(([label]) => (
                label !== 'sprints' || user?.role === User.ROLE_ADMIN
            )),
        }))
        .filter((group) => group.items.length > 0);

    // Independent CRUD tabs: hide SettingForm save/cancel
    const mustHideBottomButtons = activeTab === 'reply_templates'
        || activeTab === 'quote_text_templates';

    return (
        <div className="SettingForm bg-white dark:bg-gray-800 border-b p-4 sm:p-6">
            <main className='md:flex text-center md:text-left'>
                <div id="default-sidebar" className="z-40 md:w-64">
                    <div className="p-0 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded">
                        <div className="flex justify-center p-2">
                            <Avatar img={ logo } bordered size="lg" />
                        </div>
                        <div className="border-t">
                            {visibleGroups.map((group) => (
                                <div key={group.id}>
                                    <p className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                        {t(`settings.groups.${group.id}`)}
                                    </p>
                                    <ul className="space-y-0 font-medium text-gray-600 dark:text-gray-300">
                                        {group.items.map(([label, icon]) => (
                                            <li
                                                key={label}
                                                className={`cursor-pointer w-full text-sm py-2.5 ${(activeTab === label) ? "text-gray-900 dark:text-gray-100 bg-gray-200 dark:bg-gray-700" : "hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800"}`}
                                                onClick={() => setActiveTab(label)}
                                            >
                                                <p className="ms-3 flex items-center">
                                                    <Icon color={activeTab === label ? 'gray-900' : 'gray-500'} type={icon as IconType} className="inline mr-1" />
                                                    {t(`settings.${label}`)}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className='w-full px-4 py-4'>
                    <Formik
                        initialValues={setting}
                        validate={validate}
                        onSubmit={(values, { setSubmitting }) => {
                            onSubmit(values);
                        }}
                    >
                        {({ values }) => (
                            <Form>
                                <div className="space-y-12">
                                    { activeTab === 'auth' && <AuthForm /> }
                                    { activeTab === 'openai' && <OpenAiForm /> }
                                    { activeTab === 'apprise' && <AppriseForm /> }
                                    { activeTab === 'ticketing' && <TicketingForm /> }
                                    { activeTab === 'outline' && <OutlineForm /> }
                                    { activeTab === 'gitlab' && <GitlabForm /> }
                                    { activeTab === 'sentry' && <SentryForm /> }
                                    { activeTab === 'metabase' && <MetabaseForm /> }
                                    { activeTab === 'languagetool' && <LanguageToolForm /> }
                                    { activeTab === 'mileage' && <MileageForm /> }
                                    { activeTab === 'client_area' && <ClientAreaForm /> }
                                    { activeTab === 'email_intake' && <EmailIntakeForm /> }
                                    { activeTab === 'signal_intake' && <SignalIntakeForm /> }
                                    { activeTab === 'reply_templates' && <ReplyTemplatesForm /> }
                                    { activeTab === 'quote_text_templates' && <QuoteTextTemplatesForm /> }
                                    { activeTab === 'reminders' && <RemindersForm /> }
                                    { activeTab === 'trash_retention' && <TrashRetentionForm /> }
                                    { activeTab === 'sprints' && <SprintsForm /> }
                                </div>
                                {!mustHideBottomButtons && (
                                    <FormActions
                                        cancelTo="/"
                                        loading={updating === 'pending'}
                                    />
                                )}
                            </Form>
                        )}
                    </Formik>
                </div>
            </main>
        </div>
    );

}

export default SettingForm;
